import * as React from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseInfiniteQueryResult,
  type InfiniteData,
} from "@tanstack/react-query";
import { api } from "@/lib/api";
import { ApiError } from "@/lib/api-error";
import {
  endpoints,
  type MessageAttachmentInput,
  type MessageListResponse,
  type MessageReadResponse,
  type MessageResponse,
  type MessageSendResponse,
  type MessageUnreadCountResponse,
} from "@/lib/endpoints";
import { queryKeys } from "@/lib/query-keys";

/**
 * The messenger's data (§2's three people, behind the `messages` flag).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * POLLING, NOT WEBSOCKETS — AND THE INTERVAL IS ARGUED FROM THE BELL'S, NOT
 * COPIED FROM IT.
 *
 * Nothing in this stack does sockets, and PM2 behind nginx makes them fiddly for
 * a benefit measured against three people who are rarely in the app at the same
 * moment. So this polls, exactly as `use-notifications.ts` decided.
 *
 * THE BELL POLLS EVERY SIXTY SECONDS. THIS POLLS EVERY THIRTY, AND THE
 * DIFFERENCE IS DELIBERATE:
 *
 *   WHAT IS BEING WAITED FOR IS DIFFERENT IN KIND. A notification is a
 *   consequence of a document being saved — minutes to hours apart, and nobody
 *   is standing there expecting it. A message is one half of a conversation:
 *   when the owner asks "shall I send it today?" his son is often reading it
 *   within the minute, and a sixty-second floor makes a two-line exchange take
 *   two minutes of staring at a screen that has not changed.
 *
 *   WHAT IT COSTS is one request returning `{"unread":0}` — tens of bytes, twice
 *   a minute, on a metered workshop connection. That is still invisible next to
 *   a single PDF, which is the measure `use-notifications.ts` used.
 *
 *   WHY NOT FASTER. Below about fifteen seconds the poll starts costing battery
 *   for latency a person cannot distinguish from the network's own; and the
 *   thing being waited for is somebody typing on a phone, which is not fast.
 *
 *   AND FOCUS DOES THE REST. `refetchOnWindowFocus` is on globally
 *   (`lib/query-client.ts`), so coming back to the tab is instant regardless of
 *   where in the half-minute it lands. The interval is the floor, not the
 *   mechanism.
 *
 * THE POLL STOPS WHEN THE TAB IS HIDDEN. `refetchIntervalInBackground` is left
 * at its default of `false` — copied from the bell exactly, and worth stating
 * rather than inheriting silently: a phone in a pocket makes no requests at all.
 * ═════════════════════════════════════════════════════════════════════════════
 */
const UNREAD_POLL_MS = 30_000;

/**
 * The open conversation polls too, on the same interval and for the same reason.
 * A screen showing a conversation that silently stops updating is worse than one
 * that never claimed to: the reader assumes nothing has been said.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * BUT ONLY WHILE IT IS ONE PAGE — BECAUSE REFETCHING AN INFINITE QUERY REFETCHES
 * EVERY PAGE, NOT THE NEWEST ONE.
 *
 * This is a property of the library rather than of this code, and it is worth
 * writing down because nothing about `refetchInterval: 30_000` suggests it.
 * `infiniteQueryBehavior` (query-core 5.101.4) branches on whether the fetch
 * carries a `fetchMore` direction. `fetchNextPage` does, and fetches one page.
 * A POLL, A FOCUS REFETCH AND AN `invalidateQueries` DO NOT — they take the
 * other branch, which runs `do { … } while (currentPage < remainingPages)` with
 * `remainingPages = maxPages ?? oldPages.length`, walking the whole chain
 * sequentially so the cursors stay consistent.
 *
 * So the cost argued above — one small request twice a minute — holds for the
 * badge and for a conversation sitting on its newest page, and stops holding the
 * moment the owner taps "Show older messages". Nine taps to find what was agreed
 * last month, and every 30-second tick becomes NINE sequential requests of up to
 * fifty message bodies each: eighteen requests a minute on a metered workshop
 * connection, for a screen he is reading, not watching.
 *
 * `maxPages` IS NOT THE FIX, though it is the knob the library offers. It caps
 * the pages RETAINED as well as the pages refetched, so paging back past the cap
 * would silently drop the newest page — taking the newest message out of the
 * list, and with it the id the watermark is computed from.
 *
 * THE FIX IS TO STOP POLLING ONCE THERE IS MORE THAN ONE PAGE, and to keep the
 * screen live by other means: {@link useNewMessageSignal} watches the badge's
 * own tiny unread count and refetches the conversation ONCE, when that number
 * actually changes. Idle cost while paged back falls to tens of bytes twice a
 * minute; a message that really arrives still appears.
 * ═════════════════════════════════════════════════════════════════════════════
 */
const CONVERSATION_POLL_MS = 30_000;

/**
 * How many pages are loaded — one until the reader asks for older ones.
 *
 * Typed on the shape rather than on `InfiniteData<…>` so it can be handed both
 * the hook's `data` and the `query.state.data` the option callbacks receive,
 * whose page-param generic differs.
 */
function loadedPages(data: { pages: readonly unknown[] } | undefined): number {
  return data?.pages.length ?? 1;
}

/**
 * Whether the module exists on this server AT ALL — and how the client finds out.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THERE IS NO `useFlag`, AND THIS FEATURE DOES NOT INVENT ONE. `GET /flags`
 * needs `settings.write`, which is ADMIN-only — the OWNER does not hold it, so a
 * client-side flag check would be unanswerable for the very person the feature
 * is for. The `messages` flag ships OFF and §18.3 makes a disabled feature a
 * **404, not a 403**, so the 404 IS the answer. Notifications, payments and
 * credit notes all read their flags exactly this way.
 *
 * A 404 THEREFORE MEANS "SWITCHED OFF", NEVER "NOTHING TO SHOW". The header
 * control renders nothing at all rather than an empty badge, and the screen says
 * it is not switched on rather than showing an empty conversation — which would
 * be a promise that messages arrive here, false while the flag is off.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function isModuleOff(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

export interface MessagesUnread {
  /** How many arrived since you last looked. Zero while loading — never a flash. */
  unread: number;
  /** False when the module is off, the request failed, or nobody is asking. */
  available: boolean;
  /**
   * §18.2 — the `messages` flag is OFF, read off the 404 as this file explains.
   *
   * IT IS A SEPARATE FIELD FROM `available` BECAUSE THE TWO ANSWER DIFFERENT
   * QUESTIONS. `available` is "may I add this number to a badge", which is false
   * while the request is in flight and false when it merely failed. This is "does
   * the feature exist on this server", which is true ONLY for a 404 — and it is
   * the difference between rendering a retry button and rendering "not switched
   * on". `MessengerScreen` needs it because a reader holding `message.view` and
   * not `message.direct` has no other request to read the flag from.
   */
  moduleOff: boolean;
}

/**
 * The badge on the header control.
 *
 * `enabled` IS PASSED IN RATHER THAN READ HERE, exactly as the bell's hook does
 * it: the capability check lives in the component beside the thing it hides, and
 * the hook can be exercised on its own. Called with `false` it makes NO request
 * — a person without `message.view` must not poll a route that would 403 him
 * twice a minute, and `requireCap` writes every one of those denials to
 * `audit_log` (§2.1). A hundred and twenty audited denials an hour is how a
 * security log becomes unreadable.
 */
export function useMessagesUnread(enabled: boolean): MessagesUnread {
  const query = useQuery({
    queryKey: queryKeys.messages.unreadCount(),
    queryFn: () => api.get<MessageUnreadCountResponse>(endpoints.messagesUnreadCount),
    enabled,
    refetchInterval: UNREAD_POLL_MS,
    /*
     * The badge is worthless when stale — its whole job is to say "something was
     * said since you last looked". The global 60s `staleTime` would let a focus
     * refetch return the cached value, so it is overridden to zero here, exactly
     * as the bell's is.
     */
    staleTime: 0,
  });

  return {
    unread: query.data?.unread ?? 0,
    available: enabled && query.isSuccess,
    moduleOff: enabled && isModuleOff(query.error),
  };
}

export type MessagesQuery = UseInfiniteQueryResult<InfiniteData<MessageListResponse>, Error>;

/**
 * The conversation, newest page first and "load older" behind it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * AN INFINITE QUERY THAT PAGES BACKWARDS, WHICH IS THE UNUSUAL DIRECTION AND THE
 * RIGHT ONE HERE. Page one is the NEWEST fifty — the end of a conversation is
 * where a reader wants to be — and `fetchNextPage` walks into the past with a
 * `before` cursor. `getNextPageParam` therefore reads the OLDEST id in the page
 * it was handed, not the newest.
 *
 * THE PAGES ARE FLATTENED BY {@link flattenMessages}, which reverses the page
 * order: the query holds [newest…oldest] pages, and the screen renders
 * [oldest…newest] messages. Doing that here rather than in the component means
 * the ordering rule is stated once.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function useMessages(enabled = true): MessagesQuery {
  const query = useInfiniteQuery({
    queryKey: queryKeys.messages.list(),
    queryFn: ({ pageParam }) =>
      api.get<MessageListResponse>(endpoints.messages, {
        query: { before: pageParam ?? undefined },
      }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) =>
      // The page reads oldest-first, so [0] is the oldest row in it — the cursor
      // for whatever came before. `undefined` stops the chain.
      lastPage.hasMore && lastPage.messages.length > 0 ? lastPage.messages[0]?.id : undefined,
    enabled,
    /*
     * ONE PAGE POLLS, SEVERAL DO NOT — see `CONVERSATION_POLL_MS` for why a
     * poll on an infinite query is a request PER LOADED PAGE. The predicate
     * reads the live cache rather than a render-time value, because the
     * interval is re-read by the observer as the data changes.
     */
    refetchInterval: (live) => (loadedPages(live.state.data) > 1 ? false : CONVERSATION_POLL_MS),
    /*
     * AND FOCUS IS THE SAME REQUEST BY ANOTHER NAME. `refetchOnWindowFocus` is
     * on globally, so coming back to a conversation paged nine deep fired the
     * same nine-request fan-out on every glance at the phone. Focus freshness
     * is not lost: the unread count refetches on focus (it is never gated), and
     * {@link useNewMessageSignal} turns a CHANGE in it into exactly one
     * refetch. A focus onto an unchanged conversation now costs nothing.
     */
    refetchOnWindowFocus: (live) => loadedPages(live.state.data) <= 1,
    staleTime: 0,
  });

  useNewMessageSignal(enabled, loadedPages(query.data) > 1);

  return query;
}

/**
 * Keeps a paged-back conversation live without polling it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE BADGE'S OWN NUMBER IS THE CHEAPEST "HAS ANYTHING HAPPENED" THERE IS —
 * `{"unread":0}`, tens of bytes — and it is already being polled. This watches
 * it and refetches the conversation only when it CHANGES.
 *
 * IT COUNTS ONLY OTHER PEOPLE'S MESSAGES (`services/messages.ts` excludes the
 * reader's own), which is exactly the event that needs to reach a screen that
 * has stopped polling. The reader's own send does not move it — and does not
 * need to, because `useSendMessage` invalidates the conversation itself.
 *
 * SEEDING, NOT FIRING, ON THE FIRST VALUE. The ref starts null so the first
 * count observed after mount is recorded rather than treated as a change; the
 * mount's own refetch has already fetched the conversation. From then on any
 * movement — including one that happened while the tab was hidden and is first
 * seen on focus — is one invalidation, no matter how many pages are loaded.
 *
 * `active` IS THE POLL'S OWN STATE, INVERTED: while the conversation is polling
 * there is nothing for this to add, and invalidating alongside a poll would
 * double the requests it exists to avoid.
 * ─────────────────────────────────────────────────────────────────────────────
 */
function useNewMessageSignal(enabled: boolean, active: boolean): void {
  const queryClient = useQueryClient();
  const { unread, available } = useMessagesUnread(enabled);
  const lastSeenRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (!available) return;

    const previous = lastSeenRef.current;
    lastSeenRef.current = unread;

    if (previous === null || previous === unread) return;
    if (!active) return;

    void queryClient.invalidateQueries({ queryKey: queryKeys.messages.list() });
  }, [available, unread, active, queryClient]);
}

/**
 * Every loaded message, OLDEST FIRST.
 *
 * Page 1 is the newest, page 2 the one before it, so the PAGES are reversed and
 * their contents (already oldest-first within a page, by the server's contract)
 * are concatenated.
 */
export function flattenMessages(data: InfiniteData<MessageListResponse> | undefined): MessageResponse[] {
  if (data === undefined) return [];
  return [...data.pages].reverse().flatMap((page) => page.messages);
}

/** The freshest counts the server sent — they ride on page 1, which is `pages[0]`. */
export function conversationCounts(data: InfiniteData<MessageListResponse> | undefined): {
  total: number;
  unread: number;
} {
  const newest = data?.pages[0];
  return { total: newest?.total ?? 0, unread: newest?.unread ?? 0 };
}

export interface SendMessageVariables {
  body: string;
  attachment?: MessageAttachmentInput;
}

/**
 * Say something.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NO OPTIMISTIC UPDATE, DELIBERATELY, AND THIS IS THE ONE PLACE THAT DECISION
 * COSTS SOMETHING VISIBLE.
 *
 * An optimistic bubble appears instantly and is the right call in a chat app
 * with a send queue behind it. This build has no queue (see the compose box:
 * offline DISABLES it rather than lying about sending later), so an optimistic
 * bubble would be a message on screen that may never exist — in a conversation
 * whose entire justification is that it is written down. The failure mode is
 * somebody reading their own un-sent sentence back and assuming the other two
 * can see it.
 *
 * So the bubble appears when the SERVER has the row. The mutation is fast, the
 * compose box shows a pending state, and what is on screen is true.
 *
 * ON SUCCESS, THE WHOLE `["messages"]` PREFIX IS INVALIDATED — the conversation
 * and the badge move together. It cannot touch `["notifications"]`, and must
 * not: a message is not a compliance alert and sending one must not re-fetch
 * somebody's e-way bill inbox.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function useSendMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: SendMessageVariables) =>
      api.post<MessageSendResponse>(endpoints.messageSend, {
        body: variables.body,
        ...(variables.attachment === undefined ? {} : { attachment: variables.attachment }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.messages.all });
    },
  });
}

/**
 * Move MY watermark to the newest message on screen.
 *
 * FIRE AND FORGET, AND NOTHING WAITS FOR IT. The reader is reading; blocking
 * anything on a round trip so a badge can clear would trade the thing they came
 * for against bookkeeping. If it fails the count stays up, which is the safe
 * direction — a badge shown twice is a nuisance, a badge silently cleared hides
 * something nobody sees again.
 *
 * THE SERVER NEVER MOVES IT BACKWARDS, so a second call with a stale id (two
 * tabs, a replayed request) cannot resurrect a cleared badge.
 */
export function useMarkConversationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (upToId: number) =>
      api.post<MessageReadResponse>(endpoints.messagesRead, { upToId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.messages.unreadCount() });
    },
  });
}

/**
 * Is the device online?
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THIS EXISTS BECAUSE THE COMPOSE BOX MUST TELL THE TRUTH WHEN IT IS NOT.
 *
 * The app has Dexie offline drafts, so the tempting sentence is "messages send
 * when you are back online". THAT WOULD BE A LIE unless somebody builds the
 * queue — ordering, dedupe on retry, and the same idempotent-UUID discipline the
 * document sync layer has. v1 is online-only, so the box DISABLES and says what
 * is actually true: not sent, try again when there is signal.
 *
 * `navigator.onLine` is a weak signal — it reports the network interface, not
 * whether anything is reachable — and it is used here for exactly what it is
 * good at: turning a control off when the phone KNOWS it has no connection. A
 * send attempted on a lying "online" still fails at the API and surfaces its
 * error, which is the honest fallback.
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function useIsOnline(): boolean {
  const [online, setOnline] = React.useState(
    () => typeof navigator === "undefined" || navigator.onLine !== false,
  );

  React.useEffect(() => {
    const goOnline = (): void => {
      setOnline(true);
    };
    const goOffline = (): void => {
      setOnline(false);
    };
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  return online;
}
